import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { CheckCircle2, Download, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadFile } from "../api";
import { useLang } from "../i18n";
import { Field } from "../components/fields";
import { AttachmentInput, PublicLinkButton } from "../components/FormLink";
import SignaturePad from "../components/SignaturePad";
import FormRecordsList from "../components/FormRecordsList";
import { Plus } from "lucide-react";
import DateInput from "../components/DateInput";

export default function FormCarta({ publicToken = null }) {
  const { t } = useLang();
  const initial = {
    name: "", cip: "", nationality: "", trade_name: "", address: "",
    recipient_name: "", recipient_dv: "", recipient_ruc: "",
    date: new Date().toISOString().slice(0, 10),
    client_name: "", nip: "", email: "", signature: null,
  };
  const locationEdit = useLocation().state?.edit || null;
  const [editDoc, setEditDoc] = useState(locationEdit);
  const [showForm, setShowForm] = useState(!!locationEdit);
  const [d, setD] = useState(() => (locationEdit ? { ...initial, ...locationEdit.data } : initial));
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const [atts, setAtts] = useState([]);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setD({ ...d, [k]: e?.target ? e.target.value : e });

  const scrollToForm = () => setTimeout(() => document.querySelector('[data-testid="cr-form-wrap"]')?.scrollIntoView({ behavior: "smooth" }), 80);

  const newForm = () => {
    setEditDoc(null);
    setD(initial);
    setSaved(null);
    setShowForm(true);
    scrollToForm();
  };

  const startEdit = (doc) => {
    setEditDoc(doc);
    setD({ ...initial, ...doc.data });
    setSaved(null);
    setShowForm(true);
    scrollToForm();
  };

  const submit = async () => {
    if (!d.signature) {
      toast.error(t("forms.sign_required"));
      return;
    }
    setBusy(true);
    try {
      if (publicToken) {
        const { data } = await api.post(`/public/form/${publicToken}`, { data: d, attachments: atts });
        setDone(data.form_number);
      } else if (editDoc) {
        await api.patch(`/forms/${editDoc.id}`, { data: d });
        setSaved({ id: editDoc.id, form_number: editDoc.form_number });
        toast.success(t("forms.updated"));
      } else {
        const { data } = await api.post("/forms", { form_type: "carta_responsabilidad", data: d, attachments: atts });
        setSaved(data);
        toast.success(`${t("forms.save_success")} — ${data.form_number}`);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="ec-card p-10 text-center space-y-3 max-w-xl mx-auto" data-testid="form-public-done">
        <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
        <h2 className="font-heading text-xl font-extrabold text-slate-900">{t("forms.public_done")}</h2>
        <p className="text-sm text-slate-600">{t("forms.public_done_no")} <span className="font-mono font-bold">{done}</span></p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="form-carta-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{t("cr.title")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("cr.subtitle")}</p>
        </div>
        {!publicToken && (
          <div className="flex items-center gap-2">
            <button onClick={newForm} data-testid="cr-create-button" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C] transition-colors">
              <Plus className="w-4 h-4" /> {t("frl.new")}
            </button>
            <PublicLinkButton formType="carta_responsabilidad" testid="cr-public-link" />
          </div>
        )}
      </div>

      {!publicToken && <FormRecordsList formType="carta_responsabilidad" onEdit={startEdit} refresh={saved} testid="cr" />}

      {saved && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3" data-testid="form-saved-banner">
          <span className="text-sm text-emerald-800 font-semibold">{t("forms.doc_no")} <span className="font-mono">{saved.form_number}</span></span>
          <button onClick={() => downloadFile(`/forms/${saved.id}/pdf`, `${saved.form_number}.pdf`)} data-testid="download-form-pdf" className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#16335F] text-white text-xs font-bold hover:bg-[#033A8C]">
            <Download className="w-3.5 h-3.5" /> {t("common.download_pdf")}
          </button>
        </div>
      )}

      {(publicToken || showForm) && (
      <div className="ec-card p-6 space-y-5" data-testid="cr-form-wrap">
        <p className="text-xs text-[#033A8C] bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">{t("cr.preview_note")}</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t("cr.name")}><input className="ec-input" value={d.name} onChange={set("name")} data-testid="cr-name" /></Field>
          <Field label={t("cr.cip")}><input className="ec-input" value={d.cip} onChange={set("cip")} data-testid="cr-cip" /></Field>
          <Field label={t("cr.nationality")}><input className="ec-input" value={d.nationality} onChange={set("nationality")} data-testid="cr-nationality" /></Field>
          <Field label={t("common.date")}><DateInput className="ec-input" value={d.date} onChange={set("date")} data-testid="cr-date" /></Field>
          <Field label={t("cr.trade_name")} className="sm:col-span-2"><input className="ec-input" value={d.trade_name} onChange={set("trade_name")} data-testid="cr-trade-name" /></Field>
          <Field label={t("cr.address")} className="sm:col-span-2"><textarea className="ec-input" rows={2} value={d.address} onChange={set("address")} data-testid="cr-address" /></Field>
        </div>
        <div className="pt-4 border-t border-slate-100">
          <h3 className="font-heading font-bold text-slate-900 mb-4">{t("cr.recipient_sec")}</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label={t("cr.recipient_name")} className="sm:col-span-1"><input className="ec-input" value={d.recipient_name} onChange={set("recipient_name")} data-testid="cr-recipient-name" /></Field>
            <Field label={t("cr.recipient_dv")}><input className="ec-input" value={d.recipient_dv} onChange={set("recipient_dv")} data-testid="cr-recipient-dv" /></Field>
            <Field label={t("cr.recipient_ruc")}><input className="ec-input" value={d.recipient_ruc} onChange={set("recipient_ruc")} data-testid="cr-recipient-ruc" /></Field>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-5 pt-4 border-t border-slate-100">
          <div className="space-y-4">
            <Field label={t("cr.printed_name")}><input className="ec-input" value={d.client_name} onChange={set("client_name")} data-testid="cr-printed-name" /></Field>
            <Field label={t("auth.email")}><input className="ec-input" type="email" value={d.email} onChange={set("email")} data-testid="cr-email" /></Field>
            <Field label={t("cr.nip")}><input className="ec-input" value={d.nip} onChange={set("nip")} data-testid="cr-nip" /></Field>
            <AttachmentInput value={atts} onChange={setAtts} testid="cr-attachments" />
          </div>
          <Field label={t("forms.signature")}><SignaturePad onChange={(v) => setD((p) => ({ ...p, signature: v }))} /></Field>
        </div>
        <div className="flex justify-end">
          <button onClick={submit} disabled={busy} data-testid="cr-submit" className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#16335F] text-white text-sm font-bold hover:bg-[#033A8C] disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {busy ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </div>
      )}
    </div>
  );
}
